<template>
  <div class="space-section panel panel-bordered">
    <div class="panel-heading">
      <h3 class="panel-title">Space Pictures</h3>
    </div>

    <div class="panel-body">
      <div class="picture-grid">
        <div
          v-for="config in pictureConfigs"
          :key="config.field"
          class="picture-card"
        >
          <div class="picture-card-header">
            <div>
              <h5 class="picture-title">{{ config.label }}</h5>
              <p class="picture-helper mb-0">{{ config.helper }}</p>
            </div>
            <span v-if="uploadingField === config.field" class="uploading-text"
              >Uploading...</span
            >
          </div>

          <div class="picture-preview-wrapper">
            <img
              v-if="pictureUrl(config.field)"
              :src="pictureUrl(config.field)"
              :alt="config.label"
              class="img img-fluid picture-preview"
            />
            <div v-else class="picture-placeholder">No image uploaded</div>
          </div>

          <div class="file-input-row">
            <button
              type="button"
              class="btn btn-default btn-sm file-input-button"
              :disabled="disabled || uploadingField === config.field"
              @click="openFilePicker(config.field)"
            >
              Choose file
            </button>
            <span class="file-input-name">
              {{ selectedFileNames[config.field] || "No file chosen" }}
            </span>
          </div>

          <input
            :ref="config.field"
            type="file"
            accept="image/*"
            class="file-input-native"
            :disabled="disabled || uploadingField === config.field"
            @change="upload(config.field, config.label, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { gameService } from "@/api/client";

const pictureConfigs = [
  {
    field: "sku1_picture",
    label: "SKU1",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "sku2_picture",
    label: "SKU2",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "sku3_picture",
    label: "SKU3",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "sku4_picture",
    label: "SKU4",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "sku5_picture",
    label: "SKU5",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "sku6_picture",
    label: "SKU6",
    helper: "Recommended size 110x110 to 325x325 pixels PNG",
  },
  {
    field: "vip_picture",
    label: "VIP",
    helper: "Recommended size 150x150 pixels PNG",
  },
];

export default {
  name: "SpacePictureUploadSection",

  props: {
    gameConfig: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pictureConfigs,
      uploadingField: null,
      selectedFileNames: {},
    };
  },

  methods: {
    pictureUrl(field) {
      return (
        (this.gameConfig &&
          this.gameConfig.space &&
          this.gameConfig.space[field]) ||
        ""
      );
    },

    async upload(field, label, event) {
      const file = event.target.files[0];

      if (!file) return;

      this.$set(this.selectedFileNames, field, file.name);

      if (file.size > 1000000) {
        this.$notify("File shall not exceed 1 MB");
        this.clearInput(field);
        return;
      }

      try {
        this.uploadingField = field;
        const form = new FormData();
        form.append(field, file);

        await gameService.uploadSpaceImage(this.gameConfig.id, form);

        this.$notify(`${label} picture updated.`);
        this.$emit("updated");
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(`Upload ${label} picture error.`);
      } finally {
        this.uploadingField = null;
        this.clearInput(field);
      }
    },

    clearInput(field) {
      this.$nextTick(() => {
        const inputRef = this.$refs[field];
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

        if (input) {
          input.value = "";
        }

        this.$set(this.selectedFileNames, field, "");
      });
    },

    openFilePicker(field) {
      const inputRef = this.$refs[field];
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

      if (input) {
        input.click();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.space-section {
  margin-top: 1.5rem;
}

.picture-grid {
  gap: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.picture-card {
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #dfe4ea;
  background: #fff;
}

.picture-card-header {
  gap: 0.75rem;
  display: flex;
  min-height: 56px;
  justify-content: space-between;
}

.picture-title {
  margin-bottom: 0.25rem;
}

.picture-helper {
  color: #76838f;
  font-size: 0.85rem;
}

.uploading-text {
  color: #8a5a00;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.picture-preview-wrapper {
  display: flex;
  min-height: 180px;
  margin: 1rem 0;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px dashed #dfe4ea;
}

.picture-preview {
  max-height: 160px;
  object-fit: contain;
}

.picture-placeholder {
  color: #90a4ae;
  font-size: 0.9rem;
}

.file-input-row {
  gap: 0.75rem;
  display: flex;
  min-height: 38px;
  align-items: center;
  padding: 0.75rem 0.875rem;
  border-radius: 10px;
  border: 1px solid #dfe4ea;
  background: #fafbfc;
}

.file-input-button {
  flex: 0 0 auto;
  margin: 0;
}

.file-input-name {
  color: #76838f;
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
}

.file-input-native {
  display: none;
}
</style>
